/**
 * The horizon chart (Design Review V-4; PRD 10.3; Phase 41): the capacity heatmap folded, so
 * forty people fit on a screen without a cell losing its value.
 *
 * A heatmap cell spends its whole area on one colour and can say nothing about shape; a
 * sparkline says shape and costs a panel. A horizon row is the third answer: a band 24 pixels
 * tall in which allocation is read as **height within a fold** and the fold is read as colour,
 * so a row stays thin while a 150 per cent week is an unmistakable dark block and a ramp still
 * looks different from a spike.
 *
 * What it folds, and where:
 *
 *   - the healthy band — from `underload_below` to `caution_above` — is **blank**. It is the
 *     baseline the deviations are measured from, and a screen of blank rows is the answer a
 *     planner wants to be able to see at a glance;
 *   - above `caution_above` the warm arm grows **up from the floor** in 15-point folds: the
 *     first is the caution band, every deeper one the overloaded band, each a step more intense
 *     than the last;
 *   - below `underload_below` the cool arm hangs **down from the ceiling** in the same 15-point
 *     folds, in the under-utilized colour.
 *
 * Position is therefore redundant with hue (PRD 7.4: never colour alone), and the fold ramp is
 * built out of the reserved status colours themselves rather than a new scale, so nothing here
 * introduces a colour adjacency the palette has not already validated.
 *
 * Every number is a field of the `GET /forecast/heatmap` cell for that row and week — this
 * module folds and orders, and computes no allocation of its own. The peak, the average and the
 * row order are the small multiples' own functions, imported rather than restated, so the three
 * renderings of one response cannot disagree about which row is worst.
 *
 * Shared by the browser client, the CSV export and the tests. Pure: no DOM, no I/O.
 */
import { col } from './table.js';
import { STATUS_STATES, statusColor } from './palette.js';
import { ROW_NOUNS, bandOf, round2 } from './heatmap.js';
import { MULTIPLE_SORTS, averageOf, peakPoint, sortSeries } from './multiples.js';

/** The orders the card offers: the multiples' orders, so the two renderings agree on "worst". */
export const HORIZON_SORTS = MULTIPLE_SORTS;

/** How tall a fold is, in allocation points (V-4's 15-point steps). */
export const HORIZON_STEP = 0.15;

/** How many folds an arm draws before it saturates. Four warm folds reach 160% of capacity. */
export const HORIZON_FOLDS = 4;

/**
 * The coordinate space a row is drawn in: **percentages across, pixels down**. 24 pixels tall is
 * V-4's own figure — the point of the view is that a row stays that thin however bad the week is
 * — and the width is a percentage because the row stretches to whatever the page gives it.
 *
 * That asymmetry is deliberate. The obvious alternative, a `viewBox` with
 * `preserveAspectRatio="none"`, would scale one axis five or six times the other and drag the
 * `↑` marker and the no-capacity label out of shape with it. Percentages across leave the type
 * alone.
 */
export const ROW = Object.freeze({ width: 100, height: 24 });

/**
 * How much of its status colour the palest fold carries, as a percentage mixed against the
 * surface. The deepest carries all of it, and the steps between are even, so depth reads as
 * intensity in both themes — the light theme darkens toward the colour, the dark theme
 * brightens toward it, and neither needs a token of its own.
 */
export const FOLD_MIX_FLOOR = 45;

const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));
const hours = (v) => round2(num(v) ?? 0);
const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);

/** The fill a fold is painted with: its band's reserved colour, stepped by depth. */
export function foldFill(band, depth, count = HORIZON_FOLDS) {
  const span = Math.max(1, count - 1);
  const mix = Math.round(FOLD_MIX_FLOOR + ((depth - 1) / span) * (100 - FOLD_MIX_FLOOR));
  if (mix >= 100) return statusColor(band);
  return `color-mix(in oklab, ${statusColor(band)} ${mix}%, var(--surface))`;
}

/**
 * The folds of both arms under a run's thresholds: where each one starts and stops, which
 * reserved band it belongs to (the band of its own midpoint, so a run with different thresholds
 * still colours its folds the way its heatmap colours its cells), and what it is painted with.
 *
 * The warm arm starts at `caution_above` and climbs; the cool arm starts at `underload_below`
 * and descends, floored at zero — nobody is less than idle.
 *
 * @param {{ underload_below: number, caution_above: number, overloaded_above: number }} thresholds
 * @param {{ step?: number, count?: number }} [opts]
 */
export function foldsFor(thresholds, { step = HORIZON_STEP, count = HORIZON_FOLDS } = {}) {
  const t = thresholds ?? { underload_below: 0.85, caution_above: 1, overloaded_above: 1.15 };
  const warm = [];
  const cool = [];
  for (let depth = 1; depth <= count; depth += 1) {
    const wFrom = round2(t.caution_above + (depth - 1) * step);
    const wTo = round2(t.caution_above + depth * step);
    const wBand = bandOf((wFrom + wTo) / 2, t) ?? 'overloaded';
    warm.push({
      arm: 'warm',
      depth,
      from: wFrom,
      to: wTo,
      band: wBand,
      fill: foldFill(wBand, depth, count),
      deepest: depth === count,
    });
    const cTo = round2(Math.max(0, t.underload_below - (depth - 1) * step));
    const cFrom = round2(Math.max(0, t.underload_below - depth * step));
    const cBand = bandOf((cFrom + cTo) / 2, t) ?? 'underload';
    cool.push({
      arm: 'cool',
      depth,
      from: cFrom,
      to: cTo,
      band: cBand,
      fill: foldFill(cBand, depth, count),
      deepest: depth === count,
    });
  }
  return { warm, cool, step, count, thresholds: t, baseline: [t.underload_below, t.caution_above] };
}

/**
 * What one week looks like folded: which arm it is on, how far past the baseline it went, and
 * the share of the row's height each fold takes.
 *
 * The rule is the horizon chart's own. A fold holds `step` points of allocation; fold `d` is
 * full once the deviation reaches `d × step` and empty until it reaches `(d-1) × step`, so a
 * week at 122% is fold 1 full with fold 2 a third of the way up, and a week at 130% is folds 1
 * and 2 both full — which is why the deeper colour, drawn over the shallower, is what the eye
 * reads. The healthy band deviates by nothing and draws nothing at all.
 *
 * A week past the deepest fold saturates it and is flagged `beyond`: the whole point of folding
 * is that the row does not grow, so the magnitude past the last fold is carried in figures, not
 * in height.
 *
 * @param {number | null} utilization
 * @param {ReturnType<typeof foldsFor>} folds
 */
export function cellLayers(utilization, folds) {
  const u = num(utilization);
  const [under, over] = folds.baseline;
  if (u === null) return { arm: null, deviation: null, layers: [], bands: 0, beyond: false };
  const arm = u > over ? 'warm' : u < under ? 'cool' : null;
  if (arm === null) return { arm: null, deviation: 0, layers: [], bands: 0, beyond: false };
  const deviation = round2(arm === 'warm' ? u - over : under - u);
  const set = arm === 'warm' ? folds.warm : folds.cool;
  const layers = [];
  for (const fold of set) {
    const fraction = clamp01((deviation - (fold.depth - 1) * folds.step) / folds.step);
    if (fraction <= 0) break;
    layers.push({ ...fold, fraction: round2(fraction) });
  }
  return {
    arm,
    deviation,
    layers,
    bands: layers.length,
    beyond: deviation > folds.count * folds.step,
  };
}

/** One week of a row: the heatmap cell, and what the fold rule makes of it. */
function cellFrom(cell, index, folds) {
  const utilization = num(cell.utilization);
  const capacity = hours(cell.capacity_hours);
  const folded = cellLayers(utilization, folds);
  return {
    index,
    week_start: cell.week_start,
    week_end: cell.week_end ?? null,
    utilization,
    allocated_hours: hours(cell.allocated_hours),
    capacity_hours: capacity,
    headroom_hours: num(cell.headroom_hours),
    high_confidence_hours: hours(cell.high_confidence_hours),
    band: cell.band ?? null,
    has_capacity: capacity > 0,
    over: utilization !== null && utilization > folds.thresholds.overloaded_above,
    ...folded,
    cell,
  };
}

/** One row of the chart from a heatmap row. */
function rowFrom(source, cells, folds, { reference = false } = {}) {
  const points = (cells ?? []).map((c, i) => cellFrom(c, i, folds));
  const peak = peakPoint(points);
  const deepest = points.reduce((d, p) => Math.max(d, p.arm === 'warm' ? p.bands : 0), 0);
  return {
    key: source.key,
    label: source.label,
    entity: source.entity ?? null,
    reference,
    cells: points,
    peak,
    peak_utilization: peak?.utilization ?? null,
    peak_week: peak?.week_start ?? null,
    average: averageOf(points),
    weeks: points.length,
    weeks_over: points.filter((p) => p.over).length,
    weeks_warm: points.filter((p) => p.arm === 'warm').length,
    weeks_cool: points.filter((p) => p.arm === 'cool').length,
    weeks_without_capacity: points.filter((p) => !p.has_capacity).length,
    has_capacity: points.some((p) => p.has_capacity),
    deepest_fold: deepest,
    beyond: points.some((p) => p.beyond),
    allocated_hours: round2(points.reduce((s, p) => s + p.allocated_hours, 0)),
    capacity_hours: round2(points.reduce((s, p) => s + p.capacity_hours, 0)),
  };
}

/**
 * Everything the chart draws: the rows in their order, the company row as the reference beneath
 * them, the weeks along the bottom, and the fold scale every row is folded on.
 *
 * The fold scale is the same for every row by construction — there is no per-row scale to get
 * wrong, which is the whole economy of the view and the reason forty rows can be compared.
 *
 * @param {object} data  a `GET /forecast/heatmap` response
 * @param {{ sort?: 'peak'|'average'|'label', folds?: number, step?: number }} [opts]
 */
export function horizonModel(data, { sort = 'peak', folds: count, step } = {}) {
  const weeks = (data?.totals ?? []).map((t) => ({
    week_start: t.week_start,
    week_end: t.week_end ?? null,
    utilization: num(t.utilization),
    band: t.band ?? null,
  }));
  if (!weeks.length) return null;
  const thresholds = data.thresholds ?? {
    underload_below: 0.85,
    caution_above: 1,
    overloaded_above: 1.15,
  };
  const folds = foldsFor(thresholds, {
    count: count ?? HORIZON_FOLDS,
    step: step ?? HORIZON_STEP,
  });
  const rows = (data.rows ?? []).map((row) => rowFrom(row, row.cells, folds));
  // The company total is on the same ratio scale as the rows, so it folds the same way — as the
  // reference they are read against, never as one of them. In the company dimension the single
  // row already is the company and a second copy of it would say nothing.
  const reference =
    data.dimension === 'company'
      ? null
      : rowFrom({ key: 'company', label: 'Company' }, data.totals, folds, { reference: true });
  return {
    weeks,
    rows: sortSeries(rows, sort),
    reference,
    folds,
    thresholds,
    sort,
    dimension: data.dimension,
  };
}

/**
 * Where a week's column starts across a row, and how wide it is, as a percentage of the row:
 * weeks are equal columns, however long the window is.
 */
export function weekBand(index, count, width = ROW.width) {
  const w = width / Math.max(1, count);
  return { x: round2(index * w), width: round2(w) };
}

/**
 * A row's marks in the row coordinate space: one rect per fold per week, warm arm anchored to
 * the floor and cool arm to the ceiling, plus the weeks with no capacity, which have no ratio
 * and so no fold — they are hatched, exactly as the matrix hatches such a cell.
 *
 * Rects come back in fold order so the deeper colour is painted over the shallower one, which
 * is what makes a folded week read as its deepest fold.
 *
 * @param {ReturnType<typeof horizonModel>['rows'][number]} row
 * @param {{ width?: number, height?: number }} [opts]
 */
export function horizonGeometry(row, { width = ROW.width, height = ROW.height } = {}) {
  const count = row.cells.length;
  const cells = row.cells.map((p) => {
    const band = weekBand(p.index, count, width);
    const rects = p.layers.map((layer) => {
      const h = round2(layer.fraction * height);
      return {
        depth: layer.depth,
        band: layer.band,
        fill: layer.fill,
        x: band.x,
        width: band.width,
        // The warm arm grows up from the floor and the cool arm hangs from the ceiling, so the
        // two are told apart by position as well as by hue.
        y: p.arm === 'warm' ? round2(height - h) : 0,
        height: h,
      };
    });
    return { ...p, ...band, rects };
  });
  return {
    cells,
    gaps: cells.filter((p) => !p.has_capacity),
    beyond: cells.filter((p) => p.beyond),
    blank: cells.filter((p) => p.has_capacity && p.arm === null),
  };
}

/** The figures the card's header states for the chart. */
export function horizonSummary(model) {
  const perBand = Object.fromEntries(STATUS_STATES.map((s) => [s.key, 0]));
  let over = 0;
  let idle = 0;
  let noCapacity = 0;
  let beyond = 0;
  let worst = null;
  for (const row of model?.rows ?? []) {
    if (!row.has_capacity) noCapacity += 1;
    if (row.weeks_over > 0) over += 1;
    if (row.weeks_cool > 0) idle += 1;
    for (const cell of row.cells) {
      if (cell.band) perBand[cell.band] += 1;
      if (cell.beyond) beyond += 1;
    }
    if (row.peak && (worst === null || row.peak.utilization > worst.peak.utilization))
      worst = { row, peak: row.peak };
  }
  return {
    rows: model?.rows?.length ?? 0,
    rows_over: over,
    rows_idle: idle,
    no_capacity: noCapacity,
    cells_beyond: beyond,
    per_band: perBand,
    weeks: model?.weeks?.length ?? 0,
    worst,
  };
}

/** What a fold's range is called in the legend and the tooltip. */
export function foldLabel(fold) {
  const pct = (v) => `${Math.round(v * 100)}%`;
  if (fold.deepest)
    return fold.arm === 'warm' ? `${pct(fold.from)} and over` : `under ${pct(fold.to)}`;
  return `${pct(fold.from)} to ${pct(fold.to)}`;
}

/**
 * The sentence a folded week says, used for the tooltip's footer and the hit area's
 * screen-reader text, so the two cannot drift.
 * @param {ReturnType<typeof horizonGeometry>['cells'][number]} cell
 */
export function cellText(cell) {
  if (cell.utilization === null) return 'no capacity — no ratio to fold';
  if (cell.arm === null) return 'inside the healthy band — no fold';
  const fold = cell.layers.at(-1);
  const where = cell.arm === 'warm' ? 'over capacity' : 'under the healthy band';
  const depth = `fold ${cell.bands} of ${cell.arm === 'warm' ? 'the warm arm' : 'the cool arm'}`;
  return `${Math.round(cell.utilization * 100)}% — ${where}, ${depth} (${foldLabel(fold)})${
    cell.beyond ? ', past the deepest fold' : ''
  }`;
}

/**
 * The table view's rows: one per row and week, long format, the same numbers the folds are
 * drawn from — so the gate's cell-by-cell comparison against the matrix can be done from the
 * export rather than by eye.
 */
export function horizonExportRows(model) {
  const all = [...(model?.rows ?? [])];
  if (model?.reference) all.push(model.reference);
  const out = [];
  for (const row of all) {
    for (const cell of row.cells) {
      out.push({
        row: row.label,
        key: row.key,
        week_start: cell.week_start,
        utilization: cell.utilization,
        band: cell.band,
        arm: cell.arm,
        folds: cell.bands,
        fold_range: cell.layers.length ? foldLabel(cell.layers.at(-1)) : '',
        beyond: cell.beyond,
        allocated_hours: cell.allocated_hours,
        capacity_hours: cell.capacity_hours,
        headroom_hours: cell.headroom_hours,
        high_confidence_hours: cell.high_confidence_hours,
        peak_utilization: row.peak_utilization,
        peak_week: row.peak_week,
        average_utilization: row.average,
      });
    }
  }
  return out;
}

// ---------------------------------------------------------------------------------------------
// Table columns (Phase 37)
//
// A specification, not a rendering: the screen decorates it with `withFormats` and the CSV
// writer reads the same list, so an export cannot hold a different set of columns from the
// table it came from.

/** @param {{ dimension?: string }} opts */
export function horizonColumns({ dimension = 'company' } = {}) {
  return [
    col.text('row', (ROW_NOUNS[dimension] ?? ROW_NOUNS.company)[0]),
    col.week('week_start', 'Week', { type: 'text' }),
    col.percent('utilization', 'Allocation'),
    col.text('band', 'State', { csv: (v) => v ?? '' }),
    col.text('arm', 'Arm', { csv: (v) => v ?? '' }),
    col.count('folds', 'Folds'),
    col.text('fold_range', 'Fold range'),
    col.flag('beyond', 'Past the deepest fold', { csv: (v) => (v ? 'true' : 'false') }),
    col.hours('allocated_hours', 'Allocated'),
    col.hours('capacity_hours', 'Capacity'),
    col.hours('headroom_hours', 'Headroom'),
    col.hours('high_confidence_hours', 'High confidence'),
    col.percent('peak_utilization', 'Row peak'),
    col.week('peak_week', 'Peak week', { type: 'text' }),
    col.percent('average_utilization', 'Row average'),
  ];
}
